// components/DemoClassForm.js
import React, { useState } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const DemoRequest = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    mobile_number: '',
    email_address: '',
    syllabus: '',
    grade: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/book-demo`, formData);

      if (response.status === 201) {
        setIsSuccess(true);
        setMessage('Successfully saved! We will contact you soon.');
        // Reset form
        setFormData({
          full_name: '',
          mobile_number: '',
          email_address: '',
          syllabus: '',
          grade: ''
        });
      }
    } catch (error) {
      setIsSuccess(false);
      if (error.response) {
        // Server responded with error status
        setMessage(`Error: ${error.response.data.message || 'Please check your details and try again.'}`);
      } else if (error.request) {
        // Request was made but no response received
        setMessage('Network error. Please check your connection and try again.');
      } else {
        // Something else happened
        setMessage('An unexpected error occurred. Please try again.');
      }
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const grades = Array.from({ length: 8 }, (_, i) => i + 5); 

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="card w-full max-w-4xl bg-base-100 shadow-2xl">
        <div className="card-body p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-primary">
              Book a Demo Class
            </h2>
            <p className="text-gray-600 mt-2">
              Fill in your details and we'll get back to you shortly
            </p>
          </div>
          
          {message && (
            <div className={`alert ${isSuccess ? 'alert-success' : 'alert-error'} mb-6`}>
              <div>
                <span>{message}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Full Name */}
              <div className="form-control">
                <label htmlFor="full_name" className="label">
                  <span className="label-text font-semibold">Full Name</span>
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className="input input-bordered input-primary"
                  required
                />
              </div>

              {/* Mobile Number */}
              <div className="form-control">
                <label htmlFor="mobile_number" className="label">
                  <span className="label-text font-semibold">Mobile Number</span>
                </label>
                <input
                  type="tel"
                  id="mobile_number"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleInputChange}
                  placeholder="Enter your mobile number"
                  className="input input-bordered input-primary"
                  required
                  pattern="[0-9]{10}"
                  title="Please enter a valid 10-digit mobile number"
                />
              </div>

              {/* Email Address */}
              <div className="form-control">
                <label htmlFor="email_address" className="label">
                  <span className="label-text font-semibold">Email Address</span>
                </label>
                <input
                  type="email"
                  id="email_address"
                  name="email_address"
                  value={formData.email_address}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  className="input input-bordered input-primary"
                  required
                />
              </div>

              {/* Syllabus */}
              <div className="form-control">
                <label htmlFor="syllabus" className="label">
                  <span className="label-text font-semibold">Syllabus</span>
                </label>
                <select
                  id="syllabus"
                  name="syllabus"
                  value={formData.syllabus}
                  onChange={handleInputChange}
                  className="select select-primary select-bordered"
                  required
                >
                  <option value="">Select Syllabus</option>
                  <option value="CBSE">CBSE</option>
                  <option value="ICSE">ICSE</option>
                  <option value="KERALA STATE">KERALA STATE</option>
                </select>
              </div>

              {/* Grade */}
              <div className="form-control">
                <label htmlFor="grade" className="label">
                  <span className="label-text font-semibold">Grade</span>
                </label>
                <select
                  id="grade"
                  name="grade"
                  value={formData.grade}
                  onChange={handleInputChange}
                  className="select select-primary select-bordered"
                  required
                >
                  <option value="">Select Grade</option>
                  {grades.map(grade => (
                    <option key={grade} value={grade}>
                      Grade {grade}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Submit Button */}
            <div className="form-control mt-8">
              <button
                type="submit"
                className="btn btn-primary btn-lg flex items-center justify-center"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Booking...
                  </>
                ) : (
                  'Book a Demo Class'
                )}
              </button>
            </div>
          </form>
          
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>We respect your privacy. Your information will not be shared with third parties.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoRequest;